import React, { useEffect, useState } from "react";
import { api } from "./api";

/**
 * City assignment (build plan §9.1 #2): import a CSV, or set one person by
 * hand. Cities must be this year's (Config: Cities). No dependency on the
 * Operations Console. A preview comes first; rejected rows are listed.
 */

interface Person {
  personKey: string;
  lastName: string;
  firstName: string;
  city: string;
  citySource: string;
}

interface Plan {
  changes: { line: number; personKey: string; city: string; name: string; from: string }[];
  unchanged: number;
  rejected: { line: number; personKey: string; city: string; reason: string }[];
  applied: number;
}

function Import({ onApplied }: { onApplied: () => void }) {
  const [csv, setCsv] = useState("");
  const [fileName, setFileName] = useState("");
  const [plan, setPlan] = useState<Plan | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function send(apply: boolean) {
    setBusy(true);
    setError(null);
    setMsg(null);
    const r = await api<{ result: Plan }>("POST", "/api/cities/import", { csv, apply });
    setBusy(false);
    if (!r.ok) return setError(r.data.error ?? "Couldn't read that file.");
    setPlan(r.data.result);
    if (apply) {
      setMsg(`${r.data.result.applied} cit${r.data.result.applied === 1 ? "y" : "ies"} assigned.`);
      onApplied();
    }
  }
  return (
    <section className="section-card">
      <h3 className="mb-1 text-base font-semibold">Import a CSV</h3>
      <p className="mb-2 text-sm text-slate-600">
        Columns named <span className="font-mono">PersonKey</span> or <span className="font-mono">Reference Number</span>, and <span className="font-mono">City</span> (any order; other columns are ignored). Without a header row: exactly two columns, key then city.
      </p>
      <label htmlFor="city-file" className="field-label">CSV file</label>
      <input
        id="city-file"
        type="file"
        accept=".csv,text/csv"
        className="block w-full max-w-full text-sm"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          setPlan(null);
          setMsg(null);
          if (!f) return;
          setFileName(f.name);
          setCsv(await f.text());
        }}
      />
      {error && <p className="field-error" role="alert">{error}</p>}
      <div className="mt-2 flex flex-wrap gap-2">
        <button type="button" className="btn-secondary" disabled={!csv || busy} onClick={() => send(false)}>Preview {fileName}</button>
        {plan && plan.changes.length > 0 && !msg && (
          <button type="button" className="btn-primary" disabled={busy} onClick={() => send(true)}>Assign {plan.changes.length}</button>
        )}
      </div>
      {msg && <p className="mt-2 text-sm text-emerald-800" role="status">{msg}</p>}
      {plan && (
        <div className="mt-3 text-sm">
          <p>{plan.changes.length} to assign · {plan.unchanged} already so · {plan.rejected.length} rejected</p>
          {plan.changes.length > 0 && (
            <ul className="mt-1 list-disc pl-5">
              {plan.changes.map((c) => <li key={c.line}>{c.name} ({c.personKey}): {c.from || "Not assigned"} → {c.city}</li>)}
            </ul>
          )}
          {plan.rejected.length > 0 && (
            <>
              <p className="mt-2 font-medium text-red-900">Rejected (not imported):</p>
              <ul className="list-disc pl-5 text-red-900">
                {plan.rejected.map((r) => <li key={r.line} className="break-words">Line {r.line}: {r.personKey || "(blank)"} / {r.city || "(blank)"} — {r.reason}</li>)}
              </ul>
            </>
          )}
        </div>
      )}
    </section>
  );
}

function Row({ p, cities, onSaved }: { p: Person; cities: string[]; onSaved: (city: string, source: string) => void }) {
  const [error, setError] = useState<string | null>(null);
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
      <span className="min-w-0">
        <a className="font-medium text-program underline" href={`/station/${encodeURIComponent(p.personKey)}`}>{p.lastName}, {p.firstName}</a>
        <span className="block text-xs text-slate-500">{p.personKey}{p.citySource ? ` · ${p.citySource === "Import" ? "imported" : "set by hand"}` : ""}</span>
      </span>
      <span>
        <label className="sr-only" htmlFor={`city-${p.personKey}`}>City for {p.firstName} {p.lastName}</label>
        <select
          id={`city-${p.personKey}`}
          className="field-input w-auto"
          value={p.city}
          onChange={async (e) => {
            setError(null);
            const r = await api<{ city: string; citySource: string }>("PUT", `/api/records/${encodeURIComponent(p.personKey)}/city`, { city: e.target.value });
            if (r.ok) onSaved(r.data.city, r.data.citySource);
            else setError(r.data.error ?? "Not saved.");
          }}
        >
          <option value="">Not assigned</option>
          {cities.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {error && <span className="field-error block" role="alert">{error}</span>}
      </span>
    </li>
  );
}

export function Cities(): React.ReactElement {
  const [data, setData] = useState<{ cities: string[]; people: Person[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const load = () => void api<{ cities: string[]; people: Person[] }>("GET", "/api/cities").then((r) => (r.ok ? setData(r.data) : setError(r.data.error ?? "Could not load.")));
  useEffect(load, []);
  if (error) return <div className="section-card" role="alert"><p className="text-sm text-red-700">{error}</p></div>;
  if (!data) return <p className="text-sm text-slate-600">Loading…</p>;
  const counts = Object.fromEntries(data.cities.map((c) => [c, data.people.filter((p) => p.city === c).length]));
  const unassigned = data.people.filter((p) => !p.city).length;
  const shown = data.people.filter((p) => !filter || (filter === "-" ? !p.city : p.city === filter));
  return (
    <section>
      <h2 className="mb-1 text-xl font-semibold">Cities</h2>
      <p className="mb-4 text-sm text-slate-600">
        {data.cities.length ? data.cities.map((c) => `${c} ${counts[c]}`).join(" · ") : "No cities set yet (Config: Cities)."} · Not assigned {unassigned}
      </p>
      <Import onApplied={load} />
      <section className="section-card">
        <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
          <h3 className="text-base font-semibold">By hand</h3>
          <div>
            <label htmlFor="city-filter" className="field-label">Show</label>
            <select id="city-filter" className="field-input w-auto" value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="">Everyone</option>
              <option value="-">Not assigned</option>
              {data.cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <ul className="divide-y divide-slate-100">
          {shown.map((p) => (
            <Row key={p.personKey} p={p} cities={data.cities} onSaved={(city, citySource) => setData({ ...data, people: data.people.map((x) => (x.personKey === p.personKey ? { ...x, city, citySource } : x)) })} />
          ))}
        </ul>
      </section>
    </section>
  );
}
